import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, resolveAsset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Zap } from "lucide-react";
import CategoryTrustCaption from "@/components/CategoryTrustCaption";

const CATEGORIES = ["All", "Cleaning", "Beauty", "Childcare", "Chef", "Decor", "Grocery", "Auto", "Transport", "Handyman", "Laundry", "Moving"];

export default function ServicesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialCat = searchParams.get("category") || "All";
    const [active, setActive] = useState(initialCat);
    const [services, setServices] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get("/services").then(({ data }) => setServices(data)),
            api.get("/services/stats").then(({ data }) => setStats(data?.by_category || {})).catch(() => setStats({})),
        ]).finally(() => setLoading(false));
    }, []);

    const filtered = active === "All" ? services : services.filter((s) => s.category === active);

    const onTabClick = (cat) => {
        setActive(cat);
        if (cat === "All") setSearchParams({});
        else setSearchParams({ category: cat });
    };

    return (
        <div className="min-h-screen bg-background" data-testid="services-page">
            <Header />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
                <div className="overline mb-4">The Catalog</div>
                <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-tight max-w-3xl">
                    Choose your <span className="italic gold-grad">moment.</span>
                </h1>
                <p className="mt-6 text-base text-muted-foreground max-w-xl leading-relaxed">
                    Every service is delivered by a verified professional. Book in 60 seconds, track in real time.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6 sticky top-20 z-30 bg-background/80 backdrop-blur-xl">
                <div className="flex flex-wrap gap-2 border-b border-white/5 py-4" data-testid="category-tabs">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => onTabClick(c)}
                            className={`px-5 py-2 rounded-sm text-xs uppercase tracking-[0.25em] transition-all border ${
                                active === c
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30"
                            }`}
                            data-testid={`category-tab-${c.toLowerCase()}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }, (_, i) => (
                            <div key={`services-skeleton-${i}`} className="h-80 bg-card border border-white/5 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
                        {filtered.map((s) => (
                            <article
                                key={s.id}
                                className="group flex flex-col bg-card border border-white/5 hover:border-primary/40 transition-all duration-500 overflow-hidden"
                                data-testid={`service-card-${s.id}`}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={resolveAsset(s.image_url)}
                                        alt={s.name}
                                        className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                    <div className="absolute top-4 left-4 overline bg-black/50 backdrop-blur-md px-3 py-1.5">
                                        {s.category}
                                    </div>
                                    {s.mothers_day && (
                                        <div
                                            className="absolute top-4 right-4 px-3 py-1.5 rounded-sm bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.25em] font-semibold shadow-lg"
                                            data-testid="mothers-day-badge"
                                        >
                                            Mother's Day
                                        </div>
                                    )}
                                    {s.is_negotiable && !s.mothers_day && (
                                        <div
                                            className="absolute top-4 right-4 px-3 py-1.5 rounded-sm bg-amber-500/15 gold-text border border-primary/50 backdrop-blur-md text-[10px] uppercase tracking-[0.25em] font-semibold shadow-lg"
                                            data-testid="negotiable-badge"
                                        >
                                            Negotiable
                                        </div>
                                    )}
                                </div>
                                <div className="p-7 flex flex-col gap-4 flex-1">
                                    <h3 className="font-serif text-2xl leading-tight">{s.name}</h3>
                                    <CategoryTrustCaption stats={stats[s.category]} />
                                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                                        {s.description}
                                    </p>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1.5">
                                            <Clock size={12} strokeWidth={1.5} /> {s.duration_minutes} min
                                        </span>
                                        {s.pricing_model !== "hourly" && (
                                            <span className="flex items-center gap-1.5 gold-text">
                                                <Zap size={12} strokeWidth={1.5} /> Express avail.
                                            </span>
                                        )}
                                        {(s.add_ons || []).length > 0 && (
                                            <span className="flex items-center gap-1.5 gold-text">
                                                +{s.add_ons.length} add-ons
                                            </span>
                                        )}
                                    </div>
                                    <div className="hairline" />
                                    <div className="flex items-end justify-between">
                                        <div>
                                            <div className="overline">{s.pricing_model === "hourly" ? "From / hour" : "From"}</div>
                                            <div className="font-serif text-3xl mt-1">
                                                <span className="gold-text">$</span>
                                                {s.base_price.toLocaleString()}
                                                <span className="text-xs text-muted-foreground ml-1">
                                                    {s.pricing_model === "hourly" ? "JMD/hr" : "JMD"}
                                                </span>
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => navigate(`/book/${s.id}`)}
                                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-10 px-5"
                                            data-testid={`book-btn-${s.id}`}
                                        >
                                            Book <ArrowRight size={12} className="ml-2" strokeWidth={1.5} />
                                        </Button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="text-center py-32 text-muted-foreground" data-testid="empty-services">
                        No services in this category yet.
                    </div>
                )}
            </section>

            <Footer />
        </div>
    );
}
